import { HttpErrorResponse } from '@angular/common/http';
import { isApolloError } from '@apollo/client/errors';
import {
  APP_NAME_HEADER_KEY,
  APP_VERSION,
  APP_VERSION_HEADER_KEY,
} from '@ttc/ttc-constants/app.metadata';
import type { GraphQLError, GraphQLFormattedError } from 'graphql';

import type { StackDriverError } from '@libs/src/global-error-handler/stack-drive-error';
import { APP_NAME } from '@main-client/src/app/app.metadata';

export async function sendErrorReport(errorReport: StackDriverError) {
  try {
    errorReport.referrer = window.document.referrer;
    errorReport.url = window.location.href;
    errorReport.userAgent = window.navigator.userAgent;
  } catch (error) {
    ((errorReport.context.__errors__ ??= []) as string[]).push(
      error?.stack ?? String(error),
    );
  }
  try {
    await fetch('/__errors', {
      body: JSON.stringify(errorReport),
      headers: {
        [APP_NAME_HEADER_KEY]: APP_NAME,
        [APP_VERSION_HEADER_KEY]: APP_VERSION,
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });
  } catch (error) {
    console.error(error);
  }
}

const isHttpErrorResponse = (error: unknown): error is HttpErrorResponse =>
  'object' === typeof error &&
  null !== error &&
  'HttpErrorResponse' === (error as { name?: unknown }).name;

function extractErrorContext(error: Error): Record<string, unknown> {
  if (isHttpErrorResponse(error)) {
    return {
      httpErrorResponse: {
        body: error.error,
        status: error.status,
        url: error.url,
      },
    };
  }
  if (isApolloError(error)) {
    return {
      clientErrors: error.clientErrors.map(stringifyError),
      extraInfo: error.extraInfo,
      graphQLErrors: error.graphQLErrors.map(
        (
          gqlError: GraphQLError | (GraphQLFormattedError & { toJSON?: never }),
        ) => gqlError.toJSON?.() ?? gqlError,
      ),
      networkError: error.networkError && stringifyError(error.networkError),
      protocolErrors: error.protocolErrors,
    };
  }
  return {};
}

const stringifyError = (error: unknown) => {
  if (error instanceof Error && error.stack) {
    return error.stack;
  }
  if (isHttpErrorResponse(error)) {
    const stack = error.error instanceof Error ? '\n' + error.error.stack : '';
    return error.message + stack;
  }
  return String(error);
};

function onError(event: ErrorEvent) {
  const message = stringifyError(event.error);
  let context: Record<string, unknown>;
  try {
    context = extractErrorContext(event.error);
  } catch (error) {
    context = { __errors__: [stringifyError(error)] };
  }
  sendErrorReport({
    message,
    context: { ...context, nonAngularError: true },
  });
}

function onUnhandledRejection(event: PromiseRejectionEvent) {
  const message = stringifyError(event.reason);
  let context: Record<string, unknown>;
  try {
    context = extractErrorContext(event.reason);
  } catch (error) {
    context = { __errors__: [stringifyError(error)] };
  }
  sendErrorReport({
    message,
    context: { ...context, nonAngularError: true, promise: event.promise },
  });
}

export function addNonAngularErrorHandlers() {
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
}

export function removeNonAngularErrorHandlers() {
  window.removeEventListener('error', onError);
  window.removeEventListener('unhandledrejection', onUnhandledRejection);
}
